import type React from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import type { Graph, FlowNode } from "./types";
import { Block } from "./Block.tsx";
import { Edge, EdgeDefs } from "./Edge.tsx";
import type { Greeting } from "../api.ts";

interface CanvasProps {
  graph: Graph;
  greetings: Greeting[];
  selectedId: string | null;
  /** Non-null while the editor is in "pick a target on the canvas"
   *  mode — blocks get a crosshair cursor and the source is dimmed. */
  linkingSourceId: string | null;
  onSelect: (id: string | null) => void;
  onMove: (id: string, x: number, y: number) => void;
}

/** Extract the (sourceId, label?, targetId) triples for every edge
 *  the graph would render. Returns dangling edges too, with
 *  targetId = null, so we can warn / hide them. */
export function edgesOf(nodes: Record<string, FlowNode>): Array<{
  id: string;
  source: string;
  target: string;
  label?: string;
}> {
  const out: Array<{ id: string; source: string; target: string; label?: string }> = [];
  for (const [id, n] of Object.entries(nodes)) {
    switch (n.kind) {
      case "start":
      case "play":
      case "collect":
        if (n.next && nodes[n.next]) out.push({ id: `${id}->${n.next}`, source: id, target: n.next });
        break;
      case "menu":
        for (const [d, t] of Object.entries(n.branches)) {
          if (t && nodes[t]) out.push({ id: `${id}-d${d}`, source: id, target: t, label: d });
        }
        if (n.onTimeout && nodes[n.onTimeout]) out.push({ id: `${id}-timeout`, source: id, target: n.onTimeout, label: "timeout" });
        if (n.onInvalid && nodes[n.onInvalid]) out.push({ id: `${id}-invalid`, source: id, target: n.onInvalid, label: "invalide" });
        break;
      case "ring":
        if (n.onNoAnswer && nodes[n.onNoAnswer]) out.push({ id: `${id}-noans`, source: id, target: n.onNoAnswer, label: "sans réponse" });
        if (n.onNoOneOnline && nodes[n.onNoOneOnline]) out.push({ id: `${id}-noone`, source: id, target: n.onNoOneOnline, label: "personne en ligne" });
        break;
      case "voicemail":
      case "hangup":
        break;
    }
  }
  return out;
}

export function Canvas(props: CanvasProps): React.ReactElement {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const dragRef = useRef<
    | { kind: "pan"; startX: number; startY: number; startPanX: number; startPanY: number }
    | { kind: "block"; id: string; offsetX: number; offsetY: number }
    | null
  >(null);

  const greetingName = useCallback(
    (id: string | null): string =>
      id ? props.greetings.find((g) => g.id === id)?.name ?? "(annonce manquante)" : "(aucune)",
    [props.greetings],
  );

  // Reference other blocks by their visible number — matches the #N
  // badge on the block itself, so "1→#4" is unambiguous.
  const shortLabel = useCallback(
    (id: string): string => {
      if (!id || !props.graph.nodes[id]) return "?";
      const seq = props.graph.seqs?.[id];
      return seq != null ? `#${seq}` : "?";
    },
    [props.graph.nodes, props.graph.seqs],
  );

  const edges = useMemo(() => edgesOf(props.graph.nodes), [props.graph.nodes]);

  function svgPoint(e: React.MouseEvent | MouseEvent): { x: number; y: number } {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left - pan.x) / zoom,
      y: (e.clientY - rect.top - pan.y) / zoom,
    };
  }

  function onBackgroundMouseDown(e: React.MouseEvent): void {
    // Click on empty canvas → start panning, deselect.
    dragRef.current = {
      kind: "pan",
      startX: e.clientX,
      startY: e.clientY,
      startPanX: pan.x,
      startPanY: pan.y,
    };
    props.onSelect(null);
  }

  function onBlockMouseDown(id: string, e: React.MouseEvent): void {
    // Capture pointer & start dragging that block.
    e.stopPropagation();
    const pos = props.graph.positions[id] ?? { x: 0, y: 0 };
    const sp = svgPoint(e);
    dragRef.current = {
      kind: "block",
      id,
      offsetX: sp.x - pos.x,
      offsetY: sp.y - pos.y,
    };
  }

  function onMouseMove(e: React.MouseEvent): void {
    const d = dragRef.current;
    if (!d) return;
    if (d.kind === "pan") {
      setPan({ x: d.startPanX + (e.clientX - d.startX), y: d.startPanY + (e.clientY - d.startY) });
    } else {
      const p = svgPoint(e);
      props.onMove(d.id, Math.round(p.x - d.offsetX), Math.round(p.y - d.offsetY));
    }
  }

  function onMouseUp(): void {
    dragRef.current = null;
  }

  function onWheel(e: React.WheelEvent): void {
    // Zoom anchored on cursor position. Ignore horizontal scroll.
    e.preventDefault();
    const delta = -e.deltaY * 0.0015;
    const next = Math.max(0.4, Math.min(2, zoom * (1 + delta)));
    if (next === zoom) return;
    const svg = svgRef.current;
    if (!svg) {
      setZoom(next);
      return;
    }
    const rect = svg.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    // Adjust pan so the point under the cursor stays fixed.
    const ratio = next / zoom;
    setPan({ x: cx - (cx - pan.x) * ratio, y: cy - (cy - pan.y) * ratio });
    setZoom(next);
  }

  function fit(): void {
    const positions = Object.values(props.graph.positions);
    if (positions.length === 0) {
      setPan({ x: 0, y: 0 });
      setZoom(1);
      return;
    }
    const xs = positions.map((p) => p.x);
    const ys = positions.map((p) => p.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const maxX = Math.max(...xs) + 220;
    const maxY = Math.max(...ys) + 96;
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const padding = 40;
    const contentW = maxX - minX;
    const contentH = maxY - minY;
    const z = Math.min(
      (rect.width - 2 * padding) / contentW,
      (rect.height - 2 * padding) / contentH,
      1.5,
    );
    setZoom(z);
    setPan({
      x: padding - minX * z + (rect.width - 2 * padding - contentW * z) / 2,
      y: padding - minY * z + (rect.height - 2 * padding - contentH * z) / 2,
    });
  }

  // Fit on first mount once positions are populated.
  const fitOnceRef = useRef(false);
  if (!fitOnceRef.current && Object.keys(props.graph.positions).length > 0) {
    fitOnceRef.current = true;
    setTimeout(fit, 0);
  }

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        // Fills the available height in `.flow-canvas-wrap`, which is
        // itself a flex child sized to the locked viewport. No page
        // scroll, no fixed cap — the canvas is the page.
        height: "100%",
        minHeight: 0,
        background: "#f8fafc",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        onMouseDown={onBackgroundMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onWheel={onWheel}
        style={{ display: "block", userSelect: "none" }}
      >
        <EdgeDefs />
        <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
          {edges.map((e) => {
            const sp = props.graph.positions[e.source];
            const tp = props.graph.positions[e.target];
            if (!sp || !tp) return null;
            return (
              <Edge
                key={e.id}
                id={e.id}
                fromX={sp.x}
                fromY={sp.y}
                toX={tp.x}
                toY={tp.y}
                label={e.label}
              />
            );
          })}
          {Object.entries(props.graph.nodes).map(([id, node]) => {
            const pos = props.graph.positions[id] ?? { x: 0, y: 0 };
            return (
              <Block
                key={id}
                id={id}
                node={node}
                seq={props.graph.seqs?.[id] ?? null}
                x={pos.x}
                y={pos.y}
                isRoot={id === props.graph.rootId}
                isSelected={id === props.selectedId}
                isLinkTarget={props.linkingSourceId != null && id !== props.linkingSourceId}
                isLinkSource={props.linkingSourceId === id}
                greetingName={greetingName}
                shortLabel={shortLabel}
                onMouseDown={onBlockMouseDown}
                onClick={(blockId) => props.onSelect(blockId)}
              />
            );
          })}
        </g>
      </svg>
    </div>
  );
}
